import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Upload, FileText, Download, Bot, CheckCircle2, XCircle, PencilLine, MapPin, Loader2, Plus, Search,
  Sparkles, Link2, Target, ArrowRight, User, CalendarDays, Globe2, Quote, AlertTriangle, ExternalLink,
  ShieldCheck, ChevronDown, ChevronUp, History, ScanSearch, Info, Users2, Gauge,
} from "lucide-react";

const ALIGN_CLS = {
  "Supported by scientific evidence": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Partially supported": "bg-teal-50 text-teal-700 border-teal-200",
  "Conflicts with available evidence": "bg-red-50 text-red-700 border-red-200",
  "Extends beyond available evidence": "bg-purple-50 text-purple-700 border-purple-200",
  "Highlights an evidence gap": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Insufficient evidence for assessment": "bg-slate-100 text-slate-600 border-slate-300",
};
const FINDING_CLS = {
  "AI-generated—expert review required": "bg-violet-50 text-violet-700 border-violet-300 border-dashed",
  Approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Revised: "bg-sky-50 text-sky-700 border-sky-200",
  Rejected: "bg-red-50 text-red-600 border-red-200",
  "Second review required": "bg-amber-50 text-amber-700 border-amber-200",
  "Source verification required": "bg-amber-50 text-amber-700 border-amber-200",
};
const PENDING = "AI-generated—expert review required";
const RESOLVED = ["Approved", "Revised", "Rejected"];

const reviewSummary = (u) => {
  const total = u.findings.length;
  if (!total) return { label: u.extraction_status === "pending" ? "Not started" : "Awaiting scientific review", counts: null };
  const a = u.findings.filter((f) => f.review_status === "Approved").length;
  const rv = u.findings.filter((f) => f.review_status === "Revised").length;
  const rj = u.findings.filter((f) => f.review_status === "Rejected").length;
  const pend = total - a - rv - rj;
  if (pend === total) return { label: "Awaiting scientific review", counts: { a, rv, rj, pend } };
  if (pend > 0) return { label: "Partially reviewed", counts: { a, rv, rj, pend } };
  return { label: "Review complete", counts: { a, rv, rj, pend } };
};
const approvalStatus = (u) => {
  const r = reviewSummary(u);
  if (r.label !== "Review complete") return "In review";
  return (r.counts.a + r.counts.rv) > 0 ? "Approved for analysis" : "Excluded";
};

function ReviewActions({ upload, f, onReview }) {
  const [mode, setMode] = useState(null);
  const [text, setText] = useState(f.text);
  const [rationale, setRationale] = useState("");
  const needsRationale = ["revise", "reject"].includes(mode);
  const submit = () => {
    if (needsRationale && !rationale.trim()) { toast.error("A reviewer rationale is required"); return; }
    onReview(f.id, mode, mode === "revise" ? text : undefined, rationale);
    setMode(null); setRationale("");
  };
  if (mode) {
    return (
      <div className="mt-2 space-y-2 bg-white border border-slate-200 rounded-lg p-2.5">
        {mode === "revise" && <Textarea value={text} onChange={(e) => setText(e.target.value)} className="text-xs min-h-[60px]" data-testid={`finding-edit-${upload.id}-${f.id}`} />}
        <Textarea value={rationale} onChange={(e) => setRationale(e.target.value)} placeholder={`Reviewer rationale for this ${mode} decision *`}
          className="text-xs min-h-[44px]" data-testid={`finding-rationale-${upload.id}-${f.id}`} />
        <div className="flex gap-2">
          <Button size="sm" className="h-6 text-[10px] bg-slate-900 hover:bg-slate-800 text-white" onClick={submit} data-testid={`finding-confirm-${upload.id}-${f.id}`}>Confirm {mode}</Button>
          <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => setMode(null)}>Cancel</Button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      <button onClick={() => onReview(f.id, "approve")} data-testid={`finding-approve-${upload.id}-${f.id}`}
        className="flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-0.5 hover:bg-emerald-100"><CheckCircle2 className="w-3 h-3" />Approve</button>
      <button onClick={() => setMode("revise")} data-testid={`finding-revise-${upload.id}-${f.id}`}
        className="flex items-center gap-1 text-[10px] font-medium text-sky-700 bg-sky-50 border border-sky-200 rounded-md px-2 py-0.5 hover:bg-sky-100"><PencilLine className="w-3 h-3" />Revise</button>
      <button onClick={() => setMode("reject")} data-testid={`finding-reject-${upload.id}-${f.id}`}
        className="flex items-center gap-1 text-[10px] font-medium text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-0.5 hover:bg-red-100"><XCircle className="w-3 h-3" />Reject</button>
      <button onClick={() => onReview(f.id, "second_review")} className="text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-0.5 hover:bg-amber-100">Second review</button>
      <button onClick={() => onReview(f.id, "verify_source")} className="text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-0.5 hover:bg-amber-100">Verify source</button>
    </div>
  );
}

function FindingRow({ upload, f, onReview }) {
  const [showExcerpt, setShowExcerpt] = useState(false);
  const pending = !RESOLVED.includes(f.review_status);
  return (
    <div className={`rounded-lg border p-3 ${f.review_status === PENDING ? "border-violet-200 bg-violet-50/40" : "border-slate-100 bg-slate-50/50"}`} data-testid={`finding-${upload.id}-${f.id}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="font-mono text-[9px] text-slate-400">{f.id}</span>
        <Badge variant="outline" className={`text-[9px] font-semibold ${FINDING_CLS[f.review_status]}`} data-testid={`finding-status-${upload.id}-${f.id}`}>
          {f.review_status === PENDING && <Bot className="w-3 h-3 mr-1" />}{f.review_status}</Badge>
        <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-500 border-slate-200">{f.extraction_type}</Badge>
        {f.ai_confidence != null && <span className="text-[9px] font-mono text-violet-600" title="AI extraction confidence — separate from evidence quality">AI conf {f.ai_confidence}%</span>}
        <button onClick={() => setShowExcerpt(!showExcerpt)} data-testid={`finding-location-${upload.id}-${f.id}`}
          className="ml-auto flex items-center gap-1 text-[10px] text-teal-700 font-medium font-mono hover:underline shrink-0"><MapPin className="w-3 h-3" />{f.traceability}</button>
      </div>
      <p className="text-xs leading-relaxed mt-1.5 text-slate-700">{f.text}</p>
      {showExcerpt && (
        <div className="mt-2 bg-white border-l-2 border-teal-500 border border-slate-200 rounded-r-lg p-2.5" data-testid={`finding-excerpt-${upload.id}-${f.id}`}>
          <p className="font-mono text-[8.5px] uppercase tracking-widest text-slate-400 mb-1 flex items-center gap-1"><ScanSearch className="w-3 h-3" />Supporting source excerpt · {f.traceability}</p>
          <p className="text-[11px] text-slate-600 italic leading-snug">{f.excerpt || "Excerpt not captured — open the source document at the cited location."}</p>
        </div>
      )}
      {f.original_text && f.review_status === "Revised" && (
        <p className="text-[10px] text-slate-400 mt-1.5">Original AI extraction: <span className="line-through">{f.original_text}</span></p>
      )}
      {f.rationale && <p className="text-[10px] text-slate-500 mt-1.5"><span className="font-mono text-[8.5px] uppercase tracking-wide text-slate-400 mr-1">Reviewer rationale</span>{f.rationale}</p>}
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[10px] text-slate-400">{f.reviewer || `Assigned reviewer: ${upload.reviewer_assigned || "Unassigned"}`}</span>
      </div>
      {pending && <ReviewActions upload={upload} f={f} onReview={onReview} />}
    </div>
  );
}

const VForm = ({ label, ok, value }) => (
  <div className="flex items-center gap-1.5">
    {ok ? <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" /> : <AlertTriangle className="w-3 h-3 text-amber-600 shrink-0" />}
    <span className="text-[10.5px] text-slate-600">{label}: <span className="font-medium text-slate-800">{value}</span></span>
  </div>
);

function UploadCard({ u, onUpdated, expanded, onToggle }) {
  const [busy, setBusy] = useState("");
  const [showAudit, setShowAudit] = useState(false);
  const rs = reviewSummary(u);
  const appr = approvalStatus(u);
  const activeFindings = u.findings.filter((f) => f.review_status !== "Rejected");
  const rejected = u.findings.filter((f) => f.review_status === "Rejected");
  const approvedCount = u.findings.filter((f) => ["Approved", "Revised"].includes(f.review_status)).length;
  const allReviewed = u.findings.length > 0 && u.findings.every((f) => RESOLVED.includes(f.review_status));

  const act = (label, req) => {
    setBusy(label);
    req().then((r) => { onUpdated(r.data); toast.success(`${label} complete`); })
      .catch((e) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : `${label} failed`))
      .finally(() => setBusy(""));
  };
  const onReview = (fid, action, text, rationale) => {
    api.patch(`/evidence-uploads/${u.id}/findings/${fid}`, { action, text, rationale })
      .then((r) => { onUpdated(r.data); toast.success("Review decision recorded"); })
      .catch((e) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Review failed"));
  };
  const downloadDoc = () => {
    api.get(`/evidence-uploads/${u.id}/document`, { responseType: "blob" }).then((r) => {
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = u.document.original_filename; a.click();
      URL.revokeObjectURL(url);
    }).catch(() => toast.error("Download failed"));
  };

  return (
    <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm shadow-slate-100" data-testid={`evidence-upload-${u.id}`}>
      <button className="w-full text-left p-4" onClick={onToggle} data-testid={`evu-expand-${u.id}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
              <span className="font-mono text-[10px] text-slate-400 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5">{u.id}</span>
              <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">{u.source_type}</Badge>
              <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200">Illustrative demonstration data</Badge>
              {u.cutoff_eligibility === "Outside assessment cut-off" && (
                <Badge variant="outline" className="text-[9px] bg-red-50 text-red-700 border-red-300 font-semibold" data-testid={`cutoff-flag-${u.id}`}>Outside assessment cut-off</Badge>
              )}
            </div>
            <h4 className="text-sm font-bold text-slate-900 leading-snug">{u.title_citation}</h4>
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid={`evu-statuses-${u.id}`}>
          <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-600 border-slate-200">Processing: {u.extraction_status === "completed" ? "Complete" : "Not started"}</Badge>
          <Badge variant="outline" className={`text-[9px] ${rs.label === "Review complete" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : rs.label === "Partially reviewed" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-violet-50 text-violet-700 border-violet-200"}`}>
            Review: {rs.label}{rs.counts && rs.label === "Partially reviewed" && ` (${rs.counts.a + rs.counts.rv} approved · ${rs.counts.pend} awaiting · ${rs.counts.rj} rejected)`}
          </Badge>
          <Badge variant="outline" className={`text-[9px] ${appr === "Approved for analysis" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
            <ShieldCheck className="w-3 h-3 mr-1" />{appr}
          </Badge>
          <span className="text-[10px] text-slate-400 ml-auto">{u.pub_date || "—"} · {u.geography || "—"} · family: {u.evidence_family}</span>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-3" data-testid={`validation-panel-${u.id}`}>
            <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-2">Source validation</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-1.5">
              <VForm label="File processed" ok={u.validation?.file_processed} value={u.validation?.file_processed ? "Yes" : "No file"} />
              <VForm label="Security check" ok={u.validation?.security_check === "Passed"} value={u.validation?.security_check} />
              <VForm label="OCR" ok value={u.validation?.ocr} />
              <VForm label="Language" ok value={u.validation?.language_detected} />
              <VForm label="Metadata detected" ok={u.validation?.metadata_detected} value={u.validation?.metadata_detected ? "Yes" : "Incomplete"} />
              <VForm label="Duplicate check" ok value={u.validation?.duplicate_status} />
              <VForm label="Cut-off eligibility" ok={u.cutoff_eligibility === "Within assessment cut-off"} value={u.cutoff_eligibility} />
              <VForm label="Assigned reviewer" ok={u.reviewer_assigned !== "Unassigned"} value={u.reviewer_assigned} />
            </div>
            {u.validation?.missing_fields?.length > 0 && (
              <p className="text-[10px] text-amber-700 mt-1.5">Missing required information: {u.validation.missing_fields.join(", ")}</p>
            )}
            {u.cutoff_eligibility === "Outside assessment cut-off" && (
              <p className="text-[10px] text-red-700 mt-1.5 font-medium">Published after the project evidence cut-off — excluded from the locked assessment unless an authorised reviewer records a formal cut-off exception.</p>
            )}
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-1.5 text-xs text-slate-600">
            <span className="flex items-center gap-1.5 min-w-0"><User className="w-3 h-3 text-slate-400 shrink-0" /><span className="truncate">{u.author_speaker || "—"}</span></span>
            <span className="flex items-center gap-1.5"><CalendarDays className="w-3 h-3 text-slate-400 shrink-0" />Published {u.pub_date || "—"} · Accessed {u.date_accessed || "—"}</span>
            <span className="flex items-center gap-1.5 min-w-0"><Globe2 className="w-3 h-3 text-slate-400 shrink-0" /><span className="truncate">{u.geography || "—"} · {u.language}</span></span>
            <span className="text-[11px]">{u.doc_version} · {u.congress_journal || "—"}</span>
            <span className="text-[11px]">Access: <span className="font-medium">{u.access_class || "—"}</span></span>
            <span className="text-[11px]">Copyright: <span className="font-medium">{u.copyright_status || "—"}</span></span>
            <span className="text-[11px]">Confidentiality: <span className="font-medium">{u.confidentiality || "—"}</span></span>
            <span className="text-[11px]">Uploaded by {u.uploaded_by} · {u.date_added}</span>
          </div>
          {u.family_note && <p className="text-[10.5px] text-indigo-700 bg-indigo-50/60 border border-indigo-100 rounded-lg px-3 py-2 flex items-start gap-1.5"><Users2 className="w-3.5 h-3.5 shrink-0 mt-0.5" />{u.family_note}</p>}
          <div className="flex flex-wrap items-center gap-3">
            {u.document && (
              <button onClick={downloadDoc} className="flex items-center gap-1.5 text-xs text-teal-700 font-medium hover:text-teal-800" data-testid={`evu-doc-download-${u.id}`}>
                <FileText className="w-3.5 h-3.5" />{u.document.original_filename}<Download className="w-3 h-3" /></button>
            )}
            {u.url && <a href={u.url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-teal-700 font-medium hover:text-teal-800"><ExternalLink className="w-3.5 h-3.5" />Source link</a>}
          </div>

          <div className="flex flex-wrap gap-2">
            {u.extraction_status === "pending" && (
              <Button className="h-7 text-[11px] bg-violet-600 hover:bg-violet-700 text-white" disabled={busy !== ""}
                onClick={() => act("Extraction", () => api.post(`/evidence-uploads/${u.id}/extract`))} data-testid={`extract-btn-${u.id}`}>
                {busy === "Extraction" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}Extract candidate findings (AI)</Button>
            )}
            {u.findings.length > 0 && u.voice_connections.length === 0 && (
              allReviewed && approvedCount > 0 ? (
                <Button className="h-7 text-[11px] bg-teal-600 hover:bg-teal-700 text-white" disabled={busy !== ""}
                  onClick={() => act("Voice connection", () => api.post(`/evidence-uploads/${u.id}/connect`))} data-testid={`connect-btn-${u.id}`}>
                  {busy === "Voice connection" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Link2 className="w-3 h-3 mr-1" />}Connect approved evidence with HCP Voice (AI)</Button>
              ) : (
                <span className="flex items-center gap-1.5 text-[10.5px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1" data-testid={`connect-blocked-${u.id}`}>
                  <Info className="w-3 h-3 text-amber-600" />Stage 4 locked — all findings must complete scientific review with at least one approval</span>
              )
            )}
            {u.voice_connections.length > 0 && u.generated_insights.length === 0 && (
              <Button className="h-7 text-[11px] bg-slate-900 hover:bg-slate-800 text-white" disabled={busy !== ""}
                onClick={() => act("Insight generation", () => api.post(`/evidence-uploads/${u.id}/generate-insight`))} data-testid={`generate-btn-${u.id}`}>
                {busy === "Insight generation" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Target className="w-3 h-3 mr-1" />}Generate draft strategic implication (AI)</Button>
            )}
          </div>

          {activeFindings.length > 0 && (
            <div>
              <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-2">
                Candidate findings · {approvedCount} approved for analysis{rs.counts?.pend > 0 && ` · ${rs.counts.pend} awaiting review`}</p>
              <div className="space-y-2">{activeFindings.map((f) => <FindingRow key={f.id} upload={u} f={f} onReview={onReview} />)}</div>
            </div>
          )}

          {(rejected.length > 0 || u.audit?.length > 0) && (
            <div>
              <button onClick={() => setShowAudit(!showAudit)} data-testid={`audit-toggle-${u.id}`}
                className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 hover:text-slate-700">
                <History className="w-3.5 h-3.5" />Excluded findings & audit history ({rejected.length + (u.audit?.length || 0)}) {showAudit ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}</button>
              {showAudit && (
                <div className="mt-2 space-y-2" data-testid={`audit-section-${u.id}`}>
                  {rejected.map((f) => (
                    <div key={f.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3 opacity-80">
                      <div className="flex items-center gap-1.5"><Badge variant="outline" className={`text-[9px] ${FINDING_CLS.Rejected}`}>Rejected — excluded from all outputs</Badge><span className="font-mono text-[9px] text-slate-400">{f.id} · {f.traceability}</span></div>
                      <p className="text-[11px] text-slate-500 mt-1">{f.text}</p>
                      {f.rationale && <p className="text-[10px] text-slate-500 mt-1"><span className="font-semibold">Rationale:</span> {f.rationale} — {f.reviewer}</p>}
                    </div>
                  ))}
                  {(u.audit || []).map((a, i) => (
                    <div key={i} className="rounded-lg border border-slate-200 bg-white p-2.5 text-[10.5px] text-slate-600">
                      <span className="font-semibold text-slate-800">{a.action}</span> · {a.finding_id} · {a.reviewer} ({a.role}) · {a.timestamp?.slice(0, 16).replace("T", " ")}
                      {a.rationale && <p className="mt-0.5 text-slate-500">Rationale: {a.rationale}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {u.quality_assessment && (
            <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-3" data-testid={`quality-panel-${u.id}`}>
              <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1"><Gauge className="w-3 h-3" />Evidence-quality assessment · overall: <span className={u.quality_assessment.overall === "Not yet assessed" ? "text-slate-500" : "text-teal-700"}>{u.quality_assessment.overall}</span></p>
              {u.quality_assessment.study_design && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-1.5 mb-1.5">
                  {[["Study design", "study_design"], ["Risk of bias", "risk_of_bias"], ["Directness", "directness"], ["Precision", "precision"],
                    ["Completeness", "completeness"], ["Population applicability", "population_applicability"], ["Geographic applicability", "geographic_applicability"], ["Source reliability", "source_reliability"]].map(([l, k]) => (
                    <div key={k}><p className="text-[9px] font-mono uppercase tracking-wide text-slate-400">{l}</p><p className="text-[11px] text-slate-700">{u.quality_assessment[k]}</p></div>
                  ))}
                </div>
              )}
              <p className="text-[10.5px] text-slate-500">{u.quality_assessment.rationale}</p>
            </div>
          )}

          {(u.hcp_statements?.length > 0 || u.evidence_gaps?.length > 0 || u.limitations?.length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              {u.hcp_statements?.length > 0 && (
                <div className="border border-slate-100 rounded-lg p-3">
                  <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-1.5 flex items-center gap-1"><Quote className="w-3 h-3" />Paraphrased HCP positions</p>
                  {u.hcp_statements.map((s, i) => <p key={i} className="text-[11px] text-slate-600 leading-snug mb-1">{s} <span className="text-[9px] text-slate-400">(paraphrased — not a verbatim quotation)</span></p>)}
                </div>
              )}
              {u.evidence_gaps?.length > 0 && (
                <div className="border border-slate-100 rounded-lg p-3">
                  <p className="font-mono text-[9px] uppercase tracking-widest text-indigo-500 mb-1.5 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Evidence gaps</p>
                  {u.evidence_gaps.map((s, i) => <p key={i} className="text-[11px] text-slate-600 leading-snug mb-1">{s}</p>)}
                </div>
              )}
              {u.limitations?.length > 0 && (
                <div className="border border-slate-100 rounded-lg p-3">
                  <p className="font-mono text-[9px] uppercase tracking-widest text-amber-600 mb-1.5">Limitations</p>
                  {u.limitations.map((s, i) => <p key={i} className="text-[11px] text-slate-600 leading-snug mb-1">{s}</p>)}
                </div>
              )}
            </div>
          )}

          {u.voice_connections.length > 0 && (
            <div>
              <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1"><Link2 className="w-3 h-3" />Evidence ↔ HCP voice alignment</p>
              <div className="space-y-2">
                {u.voice_connections.map((c, i) => (
                  <div key={i} className="border border-slate-100 rounded-lg p-3" data-testid={`connection-${u.id}-${i}`}>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge variant="outline" className={`text-[9px] font-semibold ${ALIGN_CLS[c.alignment]}`}>{c.alignment}</Badge>
                      <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-500 border-slate-200">Mapping confidence: {c.mapping_confidence || "Moderate"}</Badge>
                      <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200">{c.review_status || "AI-generated mapping — review required"}</Badge>
                    </div>
                    <p className="text-xs font-semibold text-slate-800 mt-1.5">{c.hcp_perception} <span className="text-[9px] font-normal text-slate-400">(synthesised HCP perspective)</span></p>
                    <p className="text-[11px] text-slate-500 leading-snug mt-0.5">{c.rationale}</p>
                    <p className="text-[10px] text-slate-400 mt-1">Based on finding {c.finding_id} · linked HCP statement {c.insight_id}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {u.generated_insights.length > 0 && (
            <div className="bg-slate-900 rounded-lg p-4" data-testid={`generated-insights-${u.id}`}>
              <p className="font-mono text-[9px] uppercase tracking-widest text-teal-400 mb-2 flex items-center gap-1"><Target className="w-3 h-3" />Draft strategic implications</p>
              {u.generated_insights.map((g, i) => (
                <div key={i} className="mb-2 last:mb-0">
                  <div className="flex flex-wrap gap-1.5 mb-1">
                    <Badge variant="outline" className="text-[9px] bg-violet-500/15 text-violet-300 border-violet-500/40 border-dashed">{g.review_status || "AI-generated draft — expert validation required"}</Badge>
                    <Badge variant="outline" className="text-[9px] bg-slate-800 text-slate-300 border-slate-700">{g.function} · {g.priority}</Badge>
                  </div>
                  <p className="text-xs text-white leading-relaxed">{g.text}</p>
                  <p className="text-[10px] text-slate-400 mt-1">Supported by approved findings {(g.supporting_findings || []).join(", ") || "—"} · added to Strategic Implications as draft ({g.implication_id}) · not a validated output until expert review</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const EMPTY = { title_citation: "", source_type: "", url: "", author_speaker: "", pub_date: "", geography: "", asset: "Elacestrant (ORSERDU)", indication: "", citation: "", language: "English", doc_version: "v1.0", date_accessed: "", congress_journal: "", access_class: "", copyright_status: "", confidentiality: "", evidence_family: "", reviewer_assigned: "" };

function UploadEvidenceDialog({ meta, onAdded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState(null);
  const [dupAction, setDupAction] = useState("");
  const [dupReason, setDupReason] = useState("");
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = () => {
    if (!form.title_citation || !form.source_type) { toast.error("Title/citation and source type are required"); return; }
    if (dup && !dupAction) { toast.error("Choose how to handle the possible duplicate"); return; }
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (dup) { fd.append("duplicate_action", dupAction); fd.append("duplicate_reason", dupReason); }
    const file = fileRef.current?.files?.[0];
    if (file) fd.append("file", file);
    setBusy(true);
    api.post("/evidence-uploads", fd).then((r) => {
      onAdded(r.data);
      toast.success(`${r.data.id} uploaded — ${r.data.cutoff_eligibility}`);
      setForm(EMPTY); setDup(null); setDupAction(""); setOpen(false); setFileName("");
    }).catch((e) => {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.duplicate_of) { setDup(d); toast.warning("Possible duplicate detected — choose how to proceed"); }
      else toast.error(typeof d === "string" ? d : "Upload failed");
    }).finally(() => setBusy(false));
  };

  const F = ({ k, ph, type = "text", span }) => (
    <Input type={type} placeholder={ph} value={form[k]} onChange={set(k)} className={`text-xs h-8 ${span ? "sm:col-span-2" : ""}`} data-testid={`evu-form-${k}`} />
  );
  const S = ({ k, ph, options }) => (
    <Select value={form[k]} onValueChange={set(k)}>
      <SelectTrigger className="text-xs h-8" data-testid={`evu-form-${k}`}><SelectValue placeholder={ph} /></SelectTrigger>
      <SelectContent>{options.map((t) => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}</SelectContent>
    </Select>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="h-8 bg-teal-600 hover:bg-teal-700 text-white text-xs" data-testid="upload-evidence-btn"><Plus className="w-3.5 h-3.5 mr-1" />Upload evidence</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Upload evidence source</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          <F k="title_citation" ph="Source title *" span />
          <F k="citation" ph="Citation (authors, journal/congress, year)" span />
          <S k="source_type" ph="Source type *" options={meta.source_types} />
          <F k="author_speaker" ph="Author, speaker or organisation" />
          <F k="pub_date" ph="Publication / presentation date" type="date" />
          <F k="date_accessed" ph="Date accessed" type="date" />
          <F k="geography" ph="Geography" />
          <F k="language" ph="Language" />
          <F k="doc_version" ph="Document version" />
          <F k="congress_journal" ph="Congress or journal" />
          <S k="access_class" ph="Access classification" options={meta.access_classes || []} />
          <F k="copyright_status" ph="Copyright / usage status" />
          <S k="confidentiality" ph="Confidentiality classification" options={meta.confidentiality_classes || []} />
          <F k="evidence_family" ph="Evidence family / related study" />
          <F k="asset" ph="Asset" />
          <F k="indication" ph="Indication" />
          <F k="reviewer_assigned" ph="Reviewer assignment" />
          <F k="url" ph="Source link (URL)" span />
          <div className="sm:col-span-2 border border-dashed border-slate-300 rounded-lg p-3"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.[0]) { fileRef.current.files = e.dataTransfer.files; setFileName(e.dataTransfer.files[0].name); } }}>
            <p className="text-xs text-slate-500 mb-1.5 flex items-center gap-1.5"><Upload className="w-3.5 h-3.5" />Drag & drop or browse — PDF, Word, PowerPoint, Excel, CSV or text (max 25 MB)</p>
            <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.md,.rtf"
              onChange={(e) => setFileName(e.target.files?.[0]?.name || "")}
              className="text-xs text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-teal-50 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-teal-700"
              data-testid="evu-form-file" />
            {fileName && (
              <p className="text-[11px] text-slate-600 mt-1.5 flex items-center gap-2">{fileName}
                <button className="text-red-600 font-medium" onClick={() => { fileRef.current.value = ""; setFileName(""); }}>Remove</button></p>
            )}
          </div>
          {dup && (
            <div className="sm:col-span-2 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="duplicate-panel">
              <p className="text-xs font-semibold text-amber-800">Possible duplicate of {dup.duplicate_of}: {dup.duplicate_title}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                <Select value={dupAction} onValueChange={setDupAction}>
                  <SelectTrigger className="text-xs h-8 bg-white" data-testid="duplicate-action"><SelectValue placeholder="How to proceed?" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="link_existing" className="text-xs">Link to the existing source</SelectItem>
                    <SelectItem value="new_version" className="text-xs">Add as a new version</SelectItem>
                    <SelectItem value="add_family" className="text-xs">Add to the existing evidence family</SelectItem>
                    <SelectItem value="separate" className="text-xs">Continue as a separate source</SelectItem>
                  </SelectContent>
                </Select>
                <Input placeholder="Reason (required for separate source)" value={dupReason} onChange={(e) => setDupReason(e.target.value)} className="text-xs h-8 bg-white" />
              </div>
            </div>
          )}
          <p className="sm:col-span-2 text-[10px] text-violet-600 flex items-start gap-1.5">
            <Bot className="w-3.5 h-3.5 shrink-0" />
            Sources are checked for duplicates and evidence cut-off eligibility on upload. AI extraction requires MarksMan scientific review before any finding contributes to analysis. The platform never bypasses access restrictions or retrieves protected full text without permission.
          </p>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" className="h-8 text-xs" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="h-8 bg-teal-600 hover:bg-teal-700 text-white text-xs" onClick={submit} disabled={busy} data-testid="evu-form-submit">
            {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}Upload</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const TABS = [["sources", "Sources"], ["review", "Findings awaiting review"], ["mappings", "Evidence-to-voice mappings"], ["drafts", "Draft strategic implications"], ["audit", "Audit history"]];

export default function EvidenceAnalysis() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("sources");
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState("");
  const [statusF, setStatusF] = useState("all");

  useEffect(() => { api.get("/evidence-uploads").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="p-8 text-slate-400 text-sm">Loading evidence workspace…</div>;

  const onUpdated = (u) => setData((prev) => ({ ...prev, uploads: prev.uploads.map((x) => (x.id === u.id ? u : x)) }));
  const uploads = data.uploads;
  const families = new Set(uploads.map((u) => u.evidence_family)).size;
  const approvedFindings = uploads.flatMap((u) => u.findings).filter((f) => ["Approved", "Revised"].includes(f.review_status)).length;
  const pendingFindings = uploads.flatMap((u) => u.findings).filter((f) => !RESOLVED.includes(f.review_status)).length;

  const visibleSources = uploads.filter((u) => {
    if (search && !u.title_citation.toLowerCase().includes(search.toLowerCase()) && !(u.citation || "").toLowerCase().includes(search.toLowerCase())) return false;
    if (statusF !== "all" && reviewSummary(u).label !== statusF) return false;
    return true;
  });

  return (
    <div className="space-y-4" data-testid="evidence-analysis-view">
      <div className="bg-slate-900 rounded-xl p-5 border border-slate-800" data-testid="workflow-banner">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="font-heading text-base font-bold text-white">Evidence-to-insight workflow</h3>
          <UploadEvidenceDialog meta={data} onAdded={(u) => setData((prev) => ({ ...prev, uploads: [u, ...prev.uploads] }))} />
        </div>
        <div className="flex flex-wrap items-center gap-y-2">
          {data.workflow_stages.map((s, i) => (
            <div key={s} className="flex items-center">
              <span className="text-xs text-slate-200 bg-slate-800 border border-slate-700 rounded-full px-3 py-1">{i + 1}. {s}</span>
              {i < data.workflow_stages.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-teal-500 mx-1.5" />}
            </div>
          ))}
        </div>
        <p className="text-[11px] text-slate-400 mt-3 flex items-start gap-1.5 max-w-4xl">
          <Bot className="w-3.5 h-3.5 text-violet-400 shrink-0 mt-0.5" />
          AI accelerates analysis but does not replace scientific review. A source cannot reach Stage 4 or 5 until every extracted finding has completed review — draft implications remain "AI-generated draft — expert validation required" until validated. Every finding stays traceable to its exact page, table, figure, panel or slide.
        </p>
        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3" data-testid="governance-strip">
          {[["Source records", uploads.length], ["Evidence families", families], ["Approved findings", approvedFindings], ["Findings awaiting review", pendingFindings]].map(([l, v]) => (
            <span key={l} className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{l}: <span className="text-teal-300 font-bold">{v}</span></span>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5" data-testid="workspace-tabs">
        {TABS.map(([id, label]) => (
          <button key={id} data-testid={`tab-${id}`} onClick={() => setTab(id)}
            className={`text-[11px] font-semibold rounded-full px-3.5 py-1.5 border transition-colors ${tab === id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-teal-400 hover:text-teal-700"}`}>
            {label}{id === "review" && pendingFindings > 0 && <span className="ml-1.5 text-amber-500">({pendingFindings})</span>}
          </button>
        ))}
      </div>

      {tab === "sources" && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-64">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input data-testid="evu-search" placeholder="Search title, citation, author, trial…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-8 pl-8 text-xs rounded-full bg-white border-slate-200" />
            </div>
            <Select value={statusF} onValueChange={setStatusF}>
              <SelectTrigger data-testid="evu-status-filter" className="w-auto h-8 text-xs gap-1.5 rounded-full border-slate-200 bg-white"><span className="text-slate-400">Review status:</span>{statusF === "all" ? "All" : statusF}</SelectTrigger>
              <SelectContent>
                {["all", "Not started", "Awaiting scientific review", "Partially reviewed", "Review complete"].map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All" : s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-3">
            {visibleSources.map((u) => (
              <UploadCard key={u.id} u={u} onUpdated={onUpdated} expanded={expanded === u.id} onToggle={() => setExpanded(expanded === u.id ? null : u.id)} />
            ))}
            {visibleSources.length === 0 && <p className="text-sm text-slate-500 text-center py-10">No sources match.</p>}
          </div>
        </>
      )}

      {tab === "review" && (
        <div className="space-y-3" data-testid="review-tab">
          {uploads.flatMap((u) => u.findings.filter((f) => !RESOLVED.includes(f.review_status)).map((f) => (
            <div key={u.id + f.id} className="bg-white border border-slate-200/80 rounded-xl p-4">
              <p className="text-[10px] text-slate-400 mb-1.5">{u.id} · {u.title_citation}</p>
              <FindingRow upload={u} f={f} onReview={(fid, action, text, rationale) =>
                api.patch(`/evidence-uploads/${u.id}/findings/${fid}`, { action, text, rationale })
                  .then((r) => { onUpdated(r.data); toast.success("Review decision recorded"); })
                  .catch((e) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Review failed"))} />
            </div>
          )))}
          {pendingFindings === 0 && <p className="text-sm text-slate-500 text-center py-10">No findings awaiting review.</p>}
        </div>
      )}

      {tab === "mappings" && (
        <div className="space-y-2" data-testid="mappings-tab">
          {uploads.flatMap((u) => u.voice_connections.map((c, i) => (
            <div key={u.id + i} className="bg-white border border-slate-200/80 rounded-xl p-4">
              <div className="flex flex-wrap items-center gap-1.5">
                <Badge variant="outline" className={`text-[9px] font-semibold ${ALIGN_CLS[c.alignment]}`}>{c.alignment}</Badge>
                <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-500 border-slate-200">Mapping confidence: {c.mapping_confidence || "Moderate"}</Badge>
                <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200">{c.review_status || "AI-generated mapping — review required"}</Badge>
                <span className="text-[10px] text-slate-400 ml-auto">{u.id}</span>
              </div>
              <p className="text-xs font-semibold text-slate-800 mt-1.5">{c.hcp_perception} <span className="text-[9px] font-normal text-slate-400">(synthesised HCP perspective)</span></p>
              <p className="text-[11px] text-slate-500 mt-0.5">{c.rationale}</p>
            </div>
          )))}
          {uploads.every((u) => u.voice_connections.length === 0) && <p className="text-sm text-slate-500 text-center py-10">No mappings yet.</p>}
        </div>
      )}

      {tab === "drafts" && (
        <div className="space-y-2" data-testid="drafts-tab">
          {uploads.flatMap((u) => u.generated_insights.map((g, i) => (
            <div key={u.id + i} className="bg-slate-900 rounded-xl p-4">
              <div className="flex flex-wrap gap-1.5 mb-1.5">
                <Badge variant="outline" className="text-[9px] bg-violet-500/15 text-violet-300 border-violet-500/40 border-dashed">{g.review_status || "AI-generated draft — expert validation required"}</Badge>
                <Badge variant="outline" className="text-[9px] bg-slate-800 text-slate-300 border-slate-700">{g.function} · {g.priority}</Badge>
                <span className="text-[10px] text-slate-500 ml-auto">{u.id} · reviewer: {g.reviewer_assigned || u.reviewer_assigned}</span>
              </div>
              <p className="text-xs text-white leading-relaxed">{g.text}</p>
              <p className="text-[10px] text-slate-400 mt-1">Supported by approved findings {(g.supporting_findings || []).join(", ") || "—"} · {g.implication_id}</p>
            </div>
          )))}
          {uploads.every((u) => u.generated_insights.length === 0) && <p className="text-sm text-slate-500 text-center py-10">No draft implications yet.</p>}
        </div>
      )}

      {tab === "audit" && (
        <div className="space-y-2" data-testid="audit-tab">
          {uploads.flatMap((u) => (u.audit || []).map((a, i) => (
            <div key={u.id + i} className="bg-white border border-slate-200/80 rounded-xl p-3.5 text-[11px] text-slate-600">
              <span className="font-mono text-[10px] text-slate-400 mr-2">{u.id} · {a.finding_id}</span>
              <span className="font-semibold text-slate-800">{a.action}</span> by {a.reviewer} ({a.role}) · {a.timestamp?.slice(0, 16).replace("T", " ")}
              {a.original && <p className="mt-1 text-slate-500">Original: {a.original}</p>}
              {a.revised && <p className="mt-0.5 text-slate-700">Revised: {a.revised}</p>}
              {a.rationale && <p className="mt-0.5 text-slate-500">Rationale: {a.rationale}</p>}
            </div>
          )))}
          {uploads.every((u) => !(u.audit || []).length) && <p className="text-sm text-slate-500 text-center py-10">No audit entries yet.</p>}
        </div>
      )}
    </div>
  );
}
